import React, {Fragment} from "react";
import {compose} from "redux";
import {translate} from "react-i18next";
import {getLayoutAnnotationDefaultChartLayout, setLayoutAnnotationDefaultChartLayout} from "../../utils/annotations";
import {Card, Checkbox, Col, Row, Tag} from "antd";
import _ from "lodash";
import {GUTTER_MD, GUTTER_SM, MARGIN_MD, MARGIN_SM, PADDING_MD, SPAN_FULL, SPAN_HALF} from "../../styles/constants";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import DragHandle from "../drag-handle";
import CustomEmpty from "../custom-empty";
import "./style.css";

const DROPPABLE_ID_PRIMARY_DIM = 'primaryDim';
const DROPPABLE_ID_SECONDARY_DIM = 'secondaryDim';
const DROPPABLE_ID_FILTER = 'filter';

class DefaultChartLayout extends React.Component {

  constructor(props) {
    super(props);

    const layoutAnnotationDefaultChartLayout = getLayoutAnnotationDefaultChartLayout(this.props.layoutAnnotations, this.props.annotationsConfig);

    const {
      primaryDim,
      secondaryDim,
      filter
    } = layoutAnnotationDefaultChartLayout || this.getDefaultLayout();

    this.state = {
      flagSaveAnnotation: !!layoutAnnotationDefaultChartLayout,
      primaryDim,
      secondaryDim,
      filter
    };

    this.getDefaultLayout = this.getDefaultLayout.bind(this);
    this.onFlagSaveAnnotationChange = this.onFlagSaveAnnotationChange.bind(this);
  }

  static getDerivedStateFromProps(props, state) {

    if (props.dsdDimensions) {

      const {
        primaryDim,
        secondaryDim,
        filter
      } = state;

      return ({
        primaryDim: primaryDim.filter(id => props.dsdDimensions.find(dim => dim.id === id)),
        secondaryDim: secondaryDim.filter(id => props.dsdDimensions.find(dim => dim.id === id)),
        filter: [
          ...filter.filter(id => props.dsdDimensions.find(dim => dim.id === id)),
          ...props.dsdDimensions
            .filter(dim => ![...primaryDim, ...secondaryDim, ...filter].find(id => id === dim.id))
            .map(({id}) => id)
        ]
      });
    } else {
      return null;
    }
  }

  componentDidUpdate(prevProps) {

    if (this.props.dsdDimensions && (!prevProps.dsdDimensions || this.props.dsdDimensions.length !== prevProps.dsdDimensions.length)) {

      if (this.state.flagSaveAnnotation) {

        const {
          primaryDim,
          secondaryDim,
          filter
        } = this.state;

        this.props.onChange({
          layoutAnnotations: setLayoutAnnotationDefaultChartLayout(
            _.cloneDeep(this.props.layoutAnnotations),
            this.props.annotationsConfig,
            {
              primaryDim: primaryDim.filter(id => this.props.dsdDimensions.find(dim => dim.id === id)),
              secondaryDim: secondaryDim.filter(id => this.props.dsdDimensions.find(dim => dim.id === id)),
              filter: [
                ...filter.filter(id => this.props.dsdDimensions.find(dim => dim.id === id)),
                ...this.props.dsdDimensions
                  .filter(dim => ![...primaryDim, ...secondaryDim, ...filter].find(id => id === dim.id))
                  .map(({id}) => id)
              ]
            }
          )
        });
      }
    }

  }

  getDefaultLayout() {
    const TIME_PERIOD_KEY = "TIME_PERIOD";

    const dims = this.props.dsdDimensions.map(({id}) => id);
    const primaryDim = dims.includes(TIME_PERIOD_KEY)
      ? [TIME_PERIOD_KEY]
      : dims.slice(0, 1);

    return ({
      primaryDim: primaryDim,
      secondaryDim: [],
      filter: this.props.dsdDimensions
        .sort((a, b) =>
          a.order && b.order && a.order !== b.order
            ? a.order - b.order
            : a.id - b.id
        )
        .map(({id}) => id)
        .filter(id => !primaryDim.includes(id))
    });
  }

  onFlagSaveAnnotationChange(checked) {

    const {
      row,
      column,
      filter,
      rowSection
    } = this.state;

    this.props.onChange({
      layoutAnnotations: setLayoutAnnotationDefaultChartLayout(
        _.cloneDeep(this.props.layoutAnnotations),
        this.props.annotationsConfig,
        checked
          ? ({
            row,
            column,
            filter,
            rowSection
          })
          : null
      )
    });

    this.setState({
      flagSaveAnnotation: checked
    });
  }


  onDragEnd(source, destination) {

    const reorder = (list, startIndex, endIndex) => {
      const [removed] = list.splice(startIndex, 1);
      list.splice(endIndex, 0, removed);
    };

    const move = (source, destination, droppableSource, droppableDestination) => {
      const [removed] = source.splice(droppableSource.index, 1);
      destination.splice(droppableDestination.index, 0, removed);
    };

    const swap = (sourceArr, destinationArr, droppableSource) => {
      const [removedSrc] = sourceArr.splice(droppableSource.index, 1);
      const [removedDst] = destinationArr.splice(0, 1, removedSrc);
      sourceArr.splice(sourceArr.length, 0, removedDst);
    };

    if (source && destination) {

      const newLayout = {
        primaryDim: this.state.primaryDim,
        secondaryDim: this.state.secondaryDim,
        filter: this.state.filter
      };

      if (source.droppableId === destination.droppableId) {
        reorder(
          newLayout[source.droppableId],
          source.index,
          destination.index
        );

      } else if (
        (source.droppableId === DROPPABLE_ID_SECONDARY_DIM && destination.droppableId === DROPPABLE_ID_FILTER) ||
        (source.droppableId === DROPPABLE_ID_FILTER && destination.droppableId === DROPPABLE_ID_SECONDARY_DIM && newLayout[destination.droppableId].length === 0)
      ) {
        move(
          newLayout[source.droppableId],
          newLayout[destination.droppableId],
          source,
          destination
        );

      } else {
        swap(
          newLayout[source.droppableId],
          newLayout[destination.droppableId],
          source
        );
      }

      this.props.onChange({
        layoutAnnotations: setLayoutAnnotationDefaultChartLayout(
          _.cloneDeep(this.props.layoutAnnotations),
          this.props.annotationsConfig,
          newLayout
        )
      });

      this.setState({
        ...newLayout,
        flagSaveAnnotation: true
      });
    }
  }

  render() {
    const {
      t,
      disabled
    } = this.props;

    const {
      flagSaveAnnotation,
      primaryDim,
      secondaryDim,
      filter
    } = this.state;

    const Dimension = ({id, index, isDragDisabled}) => {
      return (
        <Draggable draggableId={id} index={index} isDragDisabled={isDragDisabled}>
          {provided => (
            <div ref={provided.innerRef} {...provided.draggableProps}>
              <Row type="flex" style={{marginBottom: MARGIN_SM}}>
                <Col span={SPAN_FULL}>
                  <Tag
                    style={{
                      width: "100%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                    title={id}
                  >
                    {!isDragDisabled
                      ? (
                        <span {...provided.dragHandleProps}>
                          <DragHandle/>
                        </span>
                      )
                      : null
                    }
                    {id}
                  </Tag>
                </Col>
              </Row>
            </div>
          )}
        </Draggable>
      )
    };

    const cardStyle = {
      height: "100%",
    };

    const cardBodyStyle = {
      position: "relative",
      padding: 0,
    };

    const droppableStyle = {
      position: "absolute",
      height: "100%",
      width: "100%",
      padding: PADDING_MD,
      overflowY: "auto",
      overflowX: "hidden"
    };

    const filterCard =
      <Card
        type="inner"
        title={t("components.annotationsForm.layoutAnnotationDefaultChartLayout.cards.filter.title")}
        style={{...cardStyle}}
        bodyStyle={{...cardBodyStyle, minHeight: 297}}
      >
        <Droppable droppableId={DROPPABLE_ID_FILTER}>
          {(provided, {isDraggingOver}) => (
            <div {...provided.droppableProps} ref={provided.innerRef} style={droppableStyle}>
              {filter.map((id, index) => <Dimension id={id} key={index} index={index}/>)}
              {provided.placeholder}
              {filter.length === 0 && !isDraggingOver && (
                <CustomEmpty
                  text={t("components.annotationsForm.layoutAnnotationDefaultChartLayout.cards.placeholder")}
                />
              )}
            </div>
          )}
        </Droppable>
      </Card>;

    const primaryDimCard =
      <Card
        type="inner"
        title={t("components.annotationsForm.layoutAnnotationDefaultChartLayout.cards.primaryDim.title")}
        style={{...cardStyle}}
        bodyStyle={{...cardBodyStyle, minHeight: 120}}
      >
        <Droppable droppableId={DROPPABLE_ID_PRIMARY_DIM}>
          {(provided, {isDraggingOver}) => (
            <div {...provided.droppableProps} ref={provided.innerRef} style={droppableStyle}>
              {primaryDim.map((id, index) => <Dimension id={id} key={index} index={index} isDragDisabled/>)}
              {provided.placeholder}
              {primaryDim.length === 0 && !isDraggingOver && (
                <CustomEmpty
                  text={t("components.annotationsForm.layoutAnnotationDefaultChartLayout.cards.placeholder")}
                />
              )}
            </div>
          )}
        </Droppable>
      </Card>;

    const secondaryCard =
      <Card
        type="inner"
        title={t("components.annotationsForm.layoutAnnotationDefaultChartLayout.cards.secondaryDim.title")}
        style={{...cardStyle}}
        bodyStyle={{...cardBodyStyle, minHeight: 120}}
      >
        <Droppable droppableId={DROPPABLE_ID_SECONDARY_DIM}>
          {(provided, {isDraggingOver}) => (
            <div {...provided.droppableProps} ref={provided.innerRef} style={droppableStyle}>
              {secondaryDim.map((id, index) => <Dimension id={id} key={index} index={index}/>)}
              {provided.placeholder}
              {secondaryDim.length === 0 && !isDraggingOver && (
                <CustomEmpty
                  text={t("components.annotationsForm.layoutAnnotationDefaultChartLayout.cards.placeholder")}
                />
              )}
            </div>
          )}
        </Droppable>
      </Card>;

    return (
      <Fragment>
        {!disabled && (
          <Row type="flex" gutter={GUTTER_SM} style={{marginBottom: MARGIN_MD}}>
            <Col>
              {t("components.annotationsForm.layoutAnnotationDefaultChartLayout.flagSaveAnnotation.title")}:
            </Col>
            <Col>
              <Checkbox
                checked={flagSaveAnnotation}
                onChange={e => this.onFlagSaveAnnotationChange(e.target.checked)}
              />
            </Col>
          </Row>
        )}
        <DragDropContext
          onDragEnd={({source, destination}) => this.onDragEnd(source, destination)}
        >
          <Row gutter={GUTTER_MD} type="flex">
            <Col span={SPAN_HALF}>
              {filterCard}
            </Col>
            <Col span={SPAN_HALF}>
              <Row style={{marginBottom: MARGIN_SM}}>
                {primaryDimCard}
              </Row>
              <Row>
                {secondaryCard}
              </Row>
            </Col>
          </Row>
        </DragDropContext>
      </Fragment>
    );
  }
}

export default compose(
  translate(),
)(DefaultChartLayout);



// WEBPACK FOOTER //
// ./src/components/annotations-form/DefaultChartLayout.jsx